// client/src/coms/e2ee/EncryptionConsentHost.js
// The consent screen for end-to-end encrypted servers: before joining one, and when an existing member accepts the
// server turning encryption on. It explains the server's encryption, and only after "Accept" makes sure this
// browser can hold the keys (creates the encryption identity with a recovery key, or verifies this browser with the
// recovery key). If that fails nothing is joined or accepted; there is no fallback to standard messaging.
//
// requestEncryptionConsent({ mode: 'join' | 'accept', server_name, excluded }) -> Promise<{ accepted, deviceId }>
import React, { useEffect, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { cryptoAvailable, deviceTrust, ensureDevice, restoreWithRecoveryKey, setupIdentity } from './cryptoSession';
import { RecoveryKeyPanel } from './E2eeSettings';
import './e2ee.css';

const EVENT = 'vox-e2ee-consent-request';

export function requestEncryptionConsent(opts) {
  return new Promise((resolve) => {
    try {
      window.dispatchEvent(new CustomEvent(EVENT, { detail: { opts: opts || {}, resolve } }));
    } catch {
      resolve({ accepted: false });
    }
  });
}

export default function EncryptionConsentHost() {
  const [req, setReq] = useState(null); // { opts, resolve }
  const [step, setStep] = useState('consent'); // consent | working | recovery | restore
  const [error, setError] = useState('');
  const [mx, setMx] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [restoreInput, setRestoreInput] = useState('');
  const [deviceId, setDeviceId] = useState('');

  useEffect(() => {
    const onReq = (e) => {
      const d = e && e.detail;
      if (!d || typeof d.resolve !== 'function') return;
      setReq(d);
      setStep('consent');
      setError('');
      setRecoveryKey('');
      setRestoreInput('');
    };
    window.addEventListener(EVENT, onReq);
    return () => window.removeEventListener(EVENT, onReq);
  }, []);

  if (!req) return null;
  const o = req.opts || {};
  const join = o.mode === 'join';
  const setup = o.mode === 'setup';
  const finish = (accepted) => {
    const r = req.resolve;
    setReq(null);
    r({ accepted, deviceId });
  };

  // Makes sure this browser has verified encryption keys for the account; then resolves as accepted.
  const prepare = async () => {
    setStep('working');
    setError('');
    try {
      if (!cryptoAvailable()) throw new Error('This browser cannot run end-to-end encryption (it needs WebAssembly and IndexedDB).');
      const pref = await comsApiGet('/e2ee/preference');
      const who = String((pref && pref.user_id_mx) || '');
      setMx(who);
      const c = await ensureDevice(who, { replaceRemoved: true });
      setDeviceId(c.deviceId);
      const t = await deviceTrust(who);
      if (t.deviceSigned) {
        const r = req.resolve;
        setReq(null);
        r({ accepted: true, deviceId: c.deviceId });
        return;
      }
      if (!t.serverIdentity) {
        setRecoveryKey(await setupIdentity(who));
        setStep('recovery');
        return;
      }
      setStep('restore');
    } catch (e) {
      setError(`Encryption could not be set up on this device: ${String((e && e.message) || e)}. Nothing was ${join ? 'joined' : 'accepted'}.`);
      setStep('consent');
    }
  };

  const restore = async () => {
    setStep('working');
    setError('');
    try {
      await restoreWithRecoveryKey(mx, restoreInput);
      finish(true);
    } catch (e) {
      setError(String((e && e.message) || e));
      setStep('restore');
    }
  };

  return (
    <div className="vx-e2ee-modal-backdrop" role="presentation">
      <div className="vx-e2ee-modal" role="dialog" aria-modal="true" aria-label={join ? 'Join an encrypted server' : 'Accept server encryption'} data-testid="e2ee-consent">
        {step === 'recovery' ? (
          <RecoveryKeyPanel recoveryKey={recoveryKey} onDone={() => finish(true)} />
        ) : (
          <>
            <h3 className="vx-e2ee-modal-title">
              {setup ? 'Set up end-to-end encryption on this browser' : join ? 'This server uses end-to-end encryption.' : `${o.server_name || 'This server'} turned on end-to-end encryption.`}
            </h3>
            {setup ? <p>To turn on encryption for {o.server_name || 'this server'}, this browser needs your encryption keys. They are created here and never leave your devices unencrypted.</p> : null}
            {o.server_name && join ? <p className="vx-e2ee-modal-sub">{o.server_name} · Encrypted Server</p> : null}
            <p>Messages in supported channels are encrypted and can only be read by authorised member devices.</p>
            <p>Some features, such as bots, server-side search and moderation, may work differently. These are not end-to-end encrypted:</p>
            <ul className="vx-e2ee-modal-list">
              {(o.excluded || []).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {!join && !setup ? <p>You need to accept before you can keep posting in this server’s text channels.</p> : null}
            {step === 'restore' ? (
              <div className="vx-e2ee-explain">
                <strong>Verify this browser</strong>
                <span>Your account already has an encryption identity. Enter your recovery key to use it on this browser.</span>
                <input
                  className="vx-e2ee-input"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Recovery key"
                  value={restoreInput}
                  onChange={(e) => setRestoreInput(e.target.value)}
                  data-testid="e2ee-consent-restore-input"
                />
              </div>
            ) : null}
            {error ? (
              <div className="vx-acct-error" role="alert">
                {error}
              </div>
            ) : null}
            <div className="vx-e2ee-confirm-row">
              {step === 'restore' ? (
                <button type="button" className="vx-btn small" disabled={!restoreInput.trim()} onClick={restore} data-testid="e2ee-consent-restore-btn">
                  Verify and continue
                </button>
              ) : (
                <button type="button" className="vx-btn small" disabled={step === 'working'} onClick={prepare} data-testid="e2ee-consent-accept">
                  {step === 'working' ? 'Setting up encryption…' : join ? 'Accept & Join' : setup ? 'Set up encryption' : 'Accept'}
                </button>
              )}
              <button type="button" className="vx-btn small" disabled={step === 'working'} onClick={() => finish(false)} data-testid="e2ee-consent-cancel">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Joins by invite. An end-to-end encrypted server answers 409 with e2ee_consent_required (or e2ee_device_required):
// the consent screen opens, and only after "Accept & Join" (and this browser holding verified keys) is the join
// sent again with accept_encryption. Cancelling joins nothing.
export async function joinByInvite(body) {
  return joinWithConsent('/api/coms/join-by-invite', body);
}

// The same consent flow for any join endpoint (e.g. a listed server: /api/coms/discover/servers/:key/join).
// The page must have <EncryptionConsentHost /> mounted for the consent screen to appear.
export async function joinWithConsent(url, body) {
  const post = async (extra, headers) => {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify({ ...(body || {}), ...(extra || {}) }),
    });
    const data = await res.json().catch(() => null);
    if (res.status === 401) throw Object.assign(new Error('You need to be signed in to join a server.'), { status: 401 });
    if (!res.ok || !data || data.error) throw Object.assign(new Error((data && data.error) || 'Failed to join server.'), { status: res.status, data });
    return data;
  };
  try {
    return await post();
  } catch (e) {
    const code = e && e.data && e.data.code;
    if (code !== 'e2ee_consent_required' && code !== 'e2ee_device_required') throw e;
    const choice = await requestEncryptionConsent({ mode: 'join', ...(e.data.server_encryption || {}) });
    if (!choice.accepted) throw Object.assign(new Error('You did not join: this server requires accepting end-to-end encryption.'), { code: 'e2ee_consent_declined' });
    return post({ accept_encryption: true }, { 'X-Vox-E2EE-Device': choice.deviceId });
  }
}
